import { useState } from "react";
import { Link } from "react-router-dom";
import { BriefcaseBusiness, MapPin, ArrowRight, Users, Loader2 } from "lucide-react";
import ZemelapisTierRows from "@/components/zemelapis/ZemelapisTierRows";
import GiftDialog from "@/components/zemelapis/GiftDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { zemelapisProduct } from "@/lib/zemelapisTiers";
import { usePorosCheckout } from "@/hooks/usePorosCheckout";

const ZemelapisStrip = () => {
  const { startPorosCheckout, buying } = usePorosCheckout("/");
  const { openExpressCheckout } = useCart();
  const { user } = useAuth();
  const [karjeraGiftOpen, setKarjeraGiftOpen] = useState(false);
  return (
    <section className="bg-cream py-20 border-t border-ink/5">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-ink/60">
            <MapPin className="h-4 w-4" />
            Žemėlapiai
          </span>
          <h2 className="font-marquee text-3xl md:text-4xl font-bold mt-3 leading-tight text-ink">
            Vienas sau. Kitas — karjerai. Trečias — dviese.
          </h2>
          <p className="text-ink/70 text-base mt-3 leading-relaxed">
            Ne testai ir ne horoskopai. Tai dokumentai, parašyti jūsų pačių žodžiais.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Solo */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-terracotta via-terracotta to-[#a04a32] text-cream p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(196,101,74,0.55)] flex flex-col">
            <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cream/10 blur-2xl" />
            <div className="relative flex flex-col h-full">
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-cream/80">
                <MapPin className="h-4 w-4" />
                Žemėlapis · sau
              </span>
              <h3 className="font-marquee text-2xl md:text-3xl font-bold mt-4 leading-tight">
                Asmeninis žemėlapis apie tave
              </h3>
              <p className="text-cream/85 text-base mt-4 leading-relaxed">
                Portretas apie tai, kas tu esi, ko ilgiesi ir kur eini. Užpildai anketą — gauni
                dokumentą, parašytą tavo paties žodžiais.
              </p>
              <div className="mt-auto pt-7">
                <ZemelapisTierRows tone="dark" />
                <Link
                  to="/zemelapis/solo"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-cream/85 hover:text-cream transition"
                >
                  Plačiau apie Žemėlapį <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Karjera */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-mustard via-mustard to-terracotta text-ink p-8 md:p-10 shadow-[0_20px_60px_-20px_hsl(var(--mustard)/0.45)] flex flex-col">
            <div className="relative flex flex-col h-full">
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-ink/70">
                <BriefcaseBusiness className="h-4 w-4" />
                Karjeros žemėlapis
              </span>
              <h3 className="font-marquee text-2xl md:text-3xl font-bold mt-4 leading-tight">
                Žemėlapis karjeros krypčiai
              </h3>
              <p className="text-ink/75 text-base mt-4 leading-relaxed">
                Kai reikia aiškiau pamatyti, kur esi profesiškai, kas sekina ir kokį kitą žingsnį verta daryti.
              </p>
              <div className="mt-auto pt-7">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 rounded-full px-4 py-2.5 bg-cream/70 border border-ink/[0.12]">
                    <span className="text-sm font-medium truncate text-ink">Sau · €24.90</span>
                    <button
                      type="button"
                      onClick={() => openExpressCheckout(zemelapisProduct("karjera"))}
                      className="flex-shrink-0 rounded-full px-5 py-2 text-xs font-bold tracking-[0.1em] uppercase transition bg-ink text-cream hover:bg-ink/90"
                    >
                      Pirkti
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-full px-4 py-2.5 bg-cream/70 border border-ink/[0.12]">
                    <span className="text-sm font-medium truncate text-ink">Dovana · €24.90</span>
                    <button
                      type="button"
                      onClick={() => setKarjeraGiftOpen(true)}
                      className="flex-shrink-0 rounded-full px-5 py-2 text-xs font-bold tracking-[0.1em] uppercase transition bg-ink text-cream hover:bg-ink/90"
                    >
                      Pirkti
                    </button>
                  </div>
                </div>
                <Link
                  to="/karjeros-zemelapis"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink/70 hover:text-ink transition"
                >
                  Plačiau apie Karjeros žemėlapį <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <GiftDialog
              open={karjeraGiftOpen}
              onOpenChange={setKarjeraGiftOpen}
              tier="karjera_gift"
              defaultSenderName={user?.user_metadata?.full_name || ""}
              onConfirm={(fields) => openExpressCheckout(zemelapisProduct("karjera_gift", fields))}
            />
          </div>

          {/* Poros */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-ink via-ink to-[#2c2a26] text-cream p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.45)] flex flex-col">
            <div className="pointer-events-none absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#f4c89a]/15 blur-3xl" />
            <div className="relative flex flex-col h-full">
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-cream/80">
                <Users className="h-4 w-4" />
                Poros Žemėlapis · dviese
              </span>
              <h3 className="font-marquee text-2xl md:text-3xl font-bold mt-4 leading-tight">
                Vienas žemėlapis jums dviem
              </h3>
              <p className="text-cream/85 text-base mt-4 leading-relaxed">
                Kiekvienas pildo atskirai. Kai užpildote abu — gaunate vieną bendrą dokumentą ir keturias
                savaites užduočių.
              </p>
              <div className="mt-auto pt-7">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 rounded-full px-4 py-2.5 bg-cream/[0.12] border border-cream/25">
                    <span className="text-sm font-medium truncate text-cream">Dviese · €49.90</span>
                    <button
                      type="button"
                      onClick={() => startPorosCheckout(false)}
                      disabled={buying}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold tracking-[0.1em] uppercase transition bg-cream text-ink hover:bg-white disabled:opacity-60"
                    >
                      {buying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Pirkti
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-full px-4 py-2.5 bg-cream/[0.12] border border-cream/25">
                    <span className="text-sm font-medium truncate text-cream">Dovana · €49.90</span>
                    <button
                      type="button"
                      onClick={() => startPorosCheckout(true)}
                      disabled={buying}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold tracking-[0.1em] uppercase transition bg-cream text-ink hover:bg-white disabled:opacity-60"
                    >
                      {buying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Pirkti
                    </button>
                  </div>
                </div>
                <Link
                  to="/poros-zemelapis/kodas"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-cream/85 hover:text-cream transition"
                >
                  Turiu kodą <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ZemelapisStrip;
